import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import StatCard from '../StatCard';
import { SegmentedControl } from '../SegmentedControl';
import { DETAIL_LINK, DetailCard, MUTED_TEXT } from '../ballot-initiatives/detail/detailAtoms';
import type { LegislatorData } from './types';

interface LegislativeRecordProps {
  legislator: LegislatorData;
  isPublic?: boolean;
}

/** Final votes and sponsored bills, side by side. */
const LegislativeRecord: React.FC<LegislativeRecordProps> = ({ legislator, isPublic = false }) => {
  /*
    Session vs career. Both come from one aggregate with one counting rule (final
    passage), differing only in period — so the toggle changes the question being
    asked, never the arithmetic. Session is the default: the label has to be true
    before the more interesting number is offered.
  */
  const [period, setPeriod] = useState<'session' | 'career'>('session');
  const v = period === 'session' ? legislator.stats.voting.session : legislator.stats.voting.career;
  const sp = legislator.stats.sponsored;

  // Build a link to the bills list filtered to this legislator's sponsored bills
  // for this session, with the given outcome filter. Disabled in public view
  // (the bills list is an authenticated route).
  const sponsoredLink = (outcomeParam: string): string | undefined => {
    if (isPublic) return undefined;
    const params = new URLSearchParams({ legislator_id: String(legislator.id) });
    if (legislator.session_year) params.set('session', String(legislator.session_year));
    const [key, value] = outcomeParam.split('=');
    params.set(key, value);
    return `/legislation?${params.toString()}`;
  };

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <DetailCard
        id="person-final-votes"
        title="Final votes"
        aside={(
          <span className={`tabular-nums ${MUTED_TEXT}`}>
            {v.totalVotes} votes ·{' '}
            {period === 'session' ? legislator.stats.voting.session.label || legislator.session_year : 'career'}
          </span>
        )}
      >
        <SegmentedControl<'session' | 'career'>
          value={period}
          onChange={setPeriod}
          options={[
            { id: 'session', label: 'This session' },
            { id: 'career', label: 'Career' },
          ]}
          ariaLabel="Voting record period"
          size="sm"
          className="mb-4"
        />

        <div
          className="mb-4 flex h-3 overflow-hidden rounded-full bg-background-tertiary dark:bg-background-dark-tertiary"
          role="img"
          aria-label={v.categories.map((cat) => `${cat.label} ${cat.percent}%`).join(', ')}
        >
          {v.categories.map((cat) => (
            <div key={cat.key} className={voteColors(cat.key).bar} style={{ width: `${cat.percent}%` }} />
          ))}
        </div>

        <div className="grid grid-cols-3 gap-4">
          {v.categories.map((cat) => (
            <div key={cat.key}>
              <div className={`mb-1 flex items-center gap-1.5 text-xs ${MUTED_TEXT}`}>
                <span className={`inline-block h-2 w-2 rounded-full ${voteColors(cat.key).bar}`} />
                {cat.label}
              </div>
              <div className="font-display text-2xl font-semibold tabular-nums text-text-primary dark:text-text-dark-primary">
                {cat.percent}%
              </div>
            </div>
          ))}
        </div>

        {!isPublic && (
          <Link
            to={`/legislators/${legislator.id}/votes`}
            className={`mt-5 inline-flex items-center gap-1.5 text-sm font-semibold ${DETAIL_LINK}`}
          >
            View all votes <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        )}
      </DetailCard>

      <DetailCard
        id="person-sponsored-bills"
        title="Sponsored bills"
        aside={(
          <span className={`tabular-nums ${MUTED_TEXT}`}>
            {sp.total} total · {sp.total > 0 ? Math.round((sp.passed / sp.total) * 100) : 0}% pass rate
          </span>
        )}
      >
        <div className="grid grid-cols-2 gap-2">
          <StatCard label="Passed" value={sp.passed} variant="success" to={sponsoredLink('bill_status=passed')} />
          <StatCard label="Failed" value={sp.failed} variant="danger" to={sponsoredLink('bill_status=failed')} />
          <StatCard label="Vetoed" value={sp.vetoed} variant="warning" to={sponsoredLink('bill_status=vetoed')} />
          <StatCard label="Bipartisan" value={sp.bipartisan} variant="info" to={sponsoredLink('bipartisan=true')} />
        </div>
      </DetailCard>
    </div>
  );
};

// Yes and no carry the vote's meaning; excused and absent are both "didn't vote"
// and share the muted slate.
const VOTE_BAR: Record<string, string> = {
  yes: 'bg-success-700 dark:bg-success-500',
  no: 'bg-error-700 dark:bg-error-500',
  excused: 'bg-slate-300 dark:bg-slate-500',
  absent: 'bg-slate-300 dark:bg-slate-500',
  other: 'bg-primary-300 dark:bg-primary-700',
};

function voteColors(key: string): { bar: string } {
  return { bar: VOTE_BAR[key] ?? VOTE_BAR.other };
}

export default LegislativeRecord;
